import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, Users, Calendar, ChevronRight, MessageCircle, AlertTriangle } from 'lucide-react';
import { type ApiError } from '../lib/api';
import { useClub, useJoinClub, useLeaveClub } from '../hooks/useClubs';
import { useClubMembers } from '../hooks/useMembers';
import { useAuth } from '../hooks/useAuth';
import { TopBar, EventCardSkeleton, ListItemSkeleton } from '../components';
import { Skeleton } from '../components/ui/Skeleton';
import ClubMemberInviteModal from '../components/ClubMemberInviteModal';
import { createClubDetailMobileMenu } from '../components/MobileContextMenu';



export default function ClubDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // React Query hooks
  const { data: club, isLoading: loading, error: queryError } = useClub(slug || '');
  const joinClubMutation = useJoinClub();
  const leaveClubMutation = useLeaveClub();
  
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Determine membership status
  const { club: clubData, stats, membership } = club || { club: null, stats: null, membership: null };
  const isMember = membership?.role !== 'none';
  const isLeader = membership?.role === 'leader' || membership?.is_owner;
  const canManageClub = isLeader;
  
  // Fetch club members (for preview)
  const { data: clubMembers } = useClubMembers(slug || '');

  const handleJoin = async () => {
    if (!slug) return;
    
    try {
      await joinClubMutation.mutateAsync(slug);
      setError(null);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to join club');
    }
  };

  const handleLeave = async () => {
    if (!slug) return;
    
    if (!confirm('Are you sure you want to leave this club?')) {
      return;
    }
    
    try {
      await leaveClubMutation.mutateAsync(slug);
      setError(null);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to leave club');
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Club" onBack={() => navigate('/clubs')} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-6 space-y-6">
            {/* Hero image skeleton */}
            <Skeleton variant="image" className="h-48 w-full rounded-2xl" />
            
            {/* Club name and description */}
            <div className="space-y-3">
              <Skeleton className="h-7 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="text-center space-y-2">
                  <Skeleton className="h-6 w-12 mx-auto" />
                  <Skeleton className="h-3 w-16 mx-auto" />
                </div>
              ))}
            </div>
            
            {/* Join button skeleton */}
            <Skeleton className="h-12 w-full rounded-lg" />
            
            {/* Upcoming events section */}
            <div className="space-y-4">
              <Skeleton className="h-6 w-40" />
              <div className="flex gap-3 overflow-x-auto">
                {[1, 2].map((i) => (
                  <EventCardSkeleton key={i} />
                ))}
              </div>
            </div>
            
            {/* Members section */}
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              {[1, 2, 3].map((i) => (
                <ListItemSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  const displayError = error || (queryError instanceof Error ? queryError.message : null);
  if (displayError && !clubData) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Club" onBack={() => navigate('/clubs')} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 text-text-primary text-center">
            {displayError}
          </div>
        </div>
      </div>
    );
  }

  if (!clubData) {
    return null;
  }

  // Check if any mutation is in progress
  const actionLoading = joinClubMutation.isPending || leaveClubMutation.isPending;
  
  // Member preview (first 3)
  const memberPreview = clubMembers?.slice(0, 3) || [];
  const remainingMembers = (stats?.member_count || 0) - memberPreview.length;

  // Create mobile menu configuration
  const handleMembershipToggle = isMember ? handleLeave : handleJoin;
  const clubDetailMenu = slug ? createClubDetailMobileMenu(
    navigate,
    logout,
    slug,
    isLeader || false,  // Ensure boolean
    isMember || false,  // Ensure boolean
    handleMembershipToggle
  ) : undefined;

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title="Club"
        onBack={() => navigate('/clubs')}
        mobileMenu={clubDetailMenu}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 space-y-6">
          {/* Hero Image - First Content Block */}
          <div className="relative">
            {clubData?.hero_image_url ? (
              <div className="relative w-full h-48 rounded-xl overflow-hidden shadow-card mb-4">
                <img
                  src={clubData.hero_image_url}
                  alt={clubData.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent" />
                {/* Add image upload button for leaders */}
                {canManageClub && (
                  <button 
                    onClick={() => navigate(`/clubs/${slug}/settings`)}
                    className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-lg transition-all"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                )}
              </div>
            ) : canManageClub ? (
              <div className="relative w-full h-48 bg-oro-surface-alt border-2 border-dashed border-oro-border rounded-xl overflow-hidden shadow-card mb-4 flex items-center justify-center">
                <button 
                  onClick={() => navigate(`/clubs/${slug}/settings`)}
                  className="flex flex-col items-center gap-3 text-text-muted hover:text-accent transition-colors p-8"
                >
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm font-medium">Add Cover Image</span>
                </button>
              </div>
            ) : null}
          </div>

          {/* Club Identity Card */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
            <div className="flex items-start gap-4">
              {/* Avatar with accent ring */}
              <div className="relative shrink-0">
                <div className="w-16 h-16 rounded-full bg-oro-surface-alt flex items-center justify-center overflow-hidden ring-2 ring-accent/30">
                  {clubData?.hero_image_url ? (
                    <img
                      src={clubData.hero_image_url}
                      alt={clubData.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Users className="w-8 h-8 text-accent" />
                  )}
                </div>
              </div>

              {/* Club Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-text-primary font-bold text-lg">{clubData?.name}</h2>
                    {(clubData?.city || clubData?.state || clubData?.location) && (
                      <div className="flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4 text-text-muted" />
                        <span className="text-text-secondary text-sm">
                          {clubData.location || `${clubData.city || ''}${clubData.city && clubData.state ? ', ' : ''}${clubData.state || ''}`}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <Users className="w-4 h-4 text-text-muted" />
                      <span className="text-text-muted text-sm">
                        {stats?.member_count || 0} members
                      </span>
                    </div>
                  </div>
                  
                  {/* Privacy Badge */}
                  <div className="ml-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      clubData?.visibility === 'public' 
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                        : clubData?.visibility === 'private'
                        ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    }`}>
                      {clubData?.visibility === 'public' ? '🌍 Public' : 
                       clubData?.visibility === 'private' ? '🔒 Private' : 
                       '📧 Invite Only'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Join Button - Only shown if not a member */}
            {!isMember && (
              <button
                onClick={handleJoin}
                disabled={actionLoading}
                className="w-full mt-4 px-4 py-3 rounded-lg font-medium transition-all bg-accent hover:bg-accent text-black shadow-glow"
              >
                {actionLoading ? 'Loading...' : 'Join Club'}
              </button>
            )}

            {/* Leadership Controls - Only for leaders and owners */}
            {canManageClub && (
              <div className="mt-4 pt-4 border-t border-oro-border space-y-2">
                <button
                  onClick={() => navigate(`/clubs/${slug}/events/create`)}
                  className="w-full px-4 py-3 bg-oro-gold hover:bg-oro-gold text-black font-medium rounded-lg transition-all shadow-card"
                >
                  Create Event
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="px-4 py-2.5 bg-oro-surface-alt hover:bg-oro-surface border border-oro-border text-text-primary text-sm font-medium rounded-lg transition-all"
                  >
                    Invite Members
                  </button>
                  <button
                    onClick={() => navigate(`/clubs/${slug}/settings`)}
                    className="px-4 py-2.5 bg-oro-surface-alt hover:bg-oro-surface border border-oro-border text-text-primary text-sm font-medium rounded-lg transition-all"
                  >
                    Club Settings
                  </button>
                </div>
                {membership?.is_owner && (
                  <p className="text-xs text-oro-gold text-center mt-2 font-medium">
                    👑 Club Owner
                  </p>
                )}
                {membership?.role === 'leader' && !membership?.is_owner && (
                  <p className="text-xs text-accent text-center mt-2 font-medium">
                    ⭐ Club Leader
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Description Card */}
          {clubData?.description && (
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
              <h3 className="text-text-primary font-semibold mb-2">About</h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                {clubData.description}
              </p>
            </div>
          )}

          {/* Sports & Activities */}
          {(clubData?.primary_sport || (clubData?.secondary_sports && clubData.secondary_sports.length > 0)) && (
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
              <h3 className="text-text-primary font-semibold mb-3">Sports & Activities</h3>
              <div className="flex flex-wrap gap-2">
                {clubData.primary_sport && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 text-accent border border-accent/20 rounded-full">
                    <span className="text-sm font-medium">🏃 {clubData.primary_sport}</span>
                    <span className="px-1.5 py-0.5 bg-accent/20 text-xs font-bold rounded">PRIMARY</span>
                  </div>
                )}
                {clubData.secondary_sports?.map((sport, index) => (
                  <div key={index} className="flex items-center gap-1 px-3 py-1.5 bg-oro-surface-alt text-text-secondary border border-oro-border rounded-full">
                    <span className="text-sm">⚡ {sport}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Members Preview - Only for members */}
          {isMember && memberPreview.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-text-primary font-semibold">Members</h3>
                <button
                  onClick={() => navigate(`/clubs/${slug}/members`)}
                  className="text-accent hover:text-accent text-sm transition-colors"
                >
                  View All
                </button>
              </div>
              <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
                <div className="flex items-center gap-3">
                  {memberPreview.map((member) => (
                    <div key={member.id} className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-oro-surface-alt flex items-center justify-center overflow-hidden">
                        {member.profile_image_url ? (
                          <img
                            src={member.profile_image_url}
                            alt={member.public_display_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-accent font-semibold">
                            {member.public_display_name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="text-text-muted mt-1 text-xs">
                        {member.public_display_name.split(' ')[0]}
                      </span>
                    </div>
                  ))}
                  {remainingMembers > 0 && (
                    <button
                      onClick={() => navigate(`/clubs/${slug}/members`)}
                      className="flex flex-col items-center"
                    >
                      <div className="w-12 h-12 rounded-full bg-oro-surface-alt border border-oro-border flex items-center justify-center">
                        <ChevronRight className="w-5 h-5 text-text-muted" />
                      </div>
                      <span className="text-text-muted mt-1 text-xs">
                        +{remainingMembers}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Club Chat - Only for members */}
          {isMember && (
            <div className="pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-text-primary font-semibold">Chat</h3>
              </div>
              <button
                onClick={() => navigate(`/clubs/${slug}/chat`)}
                className="w-full bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card hover:shadow-card transition-all duration-200 text-left group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-black" />
                    </div>
                    <div className="flex-1">
                      <div className="text-text-primary font-medium">Club Chat</div>
                      <div className="text-text-secondary text-sm">
                        Connect with fellow members
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-accent transition-colors" />
                </div>
              </button>
            </div>
          )}

          {/* Upcoming Events - Placeholder */}
          {isMember && stats && stats.upcoming_event_count > 0 && (
            <div className="pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-text-primary font-semibold">Upcoming Events</h3>
                <button
                  onClick={() => navigate(`/clubs/${slug}/events`)}
                  className="text-accent hover:text-accent text-sm transition-colors"
                >
                  View All
                </button>
              </div>
              <button
                onClick={() => navigate(`/clubs/${slug}/events`)}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-xl p-4 shadow-card hover:shadow-card transition-all duration-200 text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-text-muted" />
                      <span className="text-text-secondary text-sm">
                        {stats.upcoming_event_count} upcoming {stats.upcoming_event_count === 1 ? 'event' : 'events'}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-muted" />
                </div>
              </button>
            </div>
          )}

          {/* Error Message */}
          {displayError && (
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4 text-text-primary text-center text-sm">
              {displayError}
            </div>
          )}

          {/* Danger Zone - Leave Club (members only, not owners) */}
          {isMember && !membership?.is_owner && (
            <div className="mt-12 pt-8 border-t border-oro-border">
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-red-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Danger Zone
                </h3>
                <p className="text-sm text-text-muted mb-4">
                  Once you leave this club, you'll lose access to all events, chat history, and member benefits. 
                  You'll need to be re-invited to join again.
                </p>
                <button
                  onClick={handleLeave}
                  disabled={actionLoading}
                  className="px-4 py-2 rounded-lg font-medium bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 hover:border-red-500/50 active:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? 'Leaving...' : 'Leave Club'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Club Member Invite Modal */}
      {club && (
        <ClubMemberInviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          clubId={club.club.id}
          clubName={club.club.name}
        />
      )}
    </div>
  );
}
